import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, Mail, Wine, Phone, User, Search, FolderOpen, Archive, ArchiveRestore } from "lucide-react";
import { Client, useUpdateClient, useDeleteClient, useClientStats, useArchiveClient, useUnarchiveClient, useClientFacilityFolders, useClientPortalFacilities, useSetClientPortalFacility, normalizeClientCode } from "@/hooks/useClients";
import { useDocumentFacilities, useFacilities, Facility } from "@/hooks/useFacilities";
import { Checkbox } from "@/components/ui/checkbox";
import { useIsAdmin } from "@/hooks/useUserProfile";
import { useWineLots } from "@/hooks/useWineLots";
import { useVolumeUnit } from "@/hooks/useOrganization";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatDistanceToNow } from "date-fns";
import { DropboxFolderDialog } from "./DropboxFolderDialog";

interface ClientDetailSheetProps {
  client: Client | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClientUpdated?: (updatedClient: Client) => void;
}

export function ClientDetailSheet({ client: clientProp, open, onOpenChange, onClientUpdated }: ClientDetailSheetProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [lotSearchTerm, setLotSearchTerm] = useState("");
  const [dropboxDialogOpen, setDropboxDialogOpen] = useState(false);
  const [dropboxDialogFacility, setDropboxDialogFacility] = useState<Facility | null>(null);
  const [displayClient, setDisplayClient] = useState<Client | null>(clientProp);

  // Sync displayClient with prop when it changes
  useEffect(() => {
    setDisplayClient(clientProp);
  }, [clientProp]);

  const client = displayClient;

  const handleClientUpdated = (updatedClient: Client) => {
    setDisplayClient(updatedClient);
    onClientUpdated?.(updatedClient);
  };

  const updateClient = useUpdateClient();
  const deleteClient = useDeleteClient();
  const archiveClient = useArchiveClient();
  const unarchiveClient = useUnarchiveClient();
  const { data: stats } = useClientStats(client?.id || "");
  const { data: lots, isLoading: lotsLoading } = useWineLots(
    lotSearchTerm || undefined,
    client?.id,
    10
  );
  const { volumeAbbrev } = useVolumeUnit();
  const { data: documentFacilities = [] } = useDocumentFacilities();
  const isAdmin = useIsAdmin();
  const { data: facilityFolders } = useClientFacilityFolders(client?.id);
  const { data: allFacilities = [] } = useFacilities();
  const { data: portalFacilityIds = [] } = useClientPortalFacilities(client?.id);
  const setPortalFacility = useSetClientPortalFacility();

  const handleEdit = () => {
    if (client) {
      setName(client.name);
      setCode(client.code || "");
      setFirstName(client.first_name || "");
      setLastName(client.last_name || "");
      setEmail(client.contact_email || "");
      setPhone(client.phone || "");
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    if (client) {
      updateClient.mutate(
        {
          id: client.id,
          input: {
            name,
            code,
            first_name: firstName || undefined,
            last_name: lastName || undefined,
            contact_email: email || undefined,
            phone: phone || undefined,
          },
        },
        {
          onSuccess: () => setIsEditing(false),
        }
      );
    }
  };

  const handleDelete = () => {
    if (client) {
      deleteClient.mutate(client.id, {
        onSuccess: () => {
          setShowDeleteDialog(false);
          onOpenChange(false);
        },
      });
    }
  };

  const handleArchive = () => {
    if (client) {
      archiveClient.mutate(client.id, {
        onSuccess: () => {
          setShowArchiveDialog(false);
          onOpenChange(false);
        },
      });
    }
  };

  const handleUnarchive = () => {
    if (client) {
      unarchiveClient.mutate(client.id, {
        onSuccess: (updatedClient) => {
          handleClientUpdated(updatedClient);
        },
      });
    }
  };

  if (!client) return null;

  const isArchived = client.is_active === false;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Client Details</SheetTitle>
          </SheetHeader>

          <div className="space-y-6 py-6">
            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-name">Company/Client Name</Label>
                  <Input
                    id="edit-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Napa Valley Estates"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-code">Client Code *</Label>
                  <Input
                    id="edit-code"
                    value={code}
                    onChange={(e) => setCode(normalizeClientCode(e.target.value))}
                    placeholder="e.g., AF"
                    maxLength={4}
                  />
                  {client.code && code !== client.code && (
                    <p className="text-xs text-amber-600 mt-1">
                      Changing a client's code only affects new documents — files already routed to Dropbox keep their old names.
                    </p>
                  )}
                </div>

                <Separator />
                <h4 className="text-sm font-medium">Primary Contact</h4>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-first-name">First Name</Label>
                    <Input
                      id="edit-first-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-last-name">Last Name</Label>
                    <Input
                      id="edit-last-name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Smith"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="john.smith@example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-phone">Phone Number</Label>
                  <Input
                    id="edit-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div className="flex gap-2">
                  <Button onClick={handleSave} disabled={updateClient.isPending || !name.trim() || !code}>
                    Save Changes
                  </Button>
                  <Button variant="outline" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-2xl font-bold">{client.name}</h3>
                    {client.code ? (
                      <Badge variant="outline" className="font-mono">{client.code}</Badge>
                    ) : (
                      <Badge variant="secondary" className="bg-amber-100 text-amber-800">No code</Badge>
                    )}
                    {isArchived && (
                      <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                        Archived
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">Company/Organization</p>
                </div>

                <Separator />

                {(client.first_name || client.last_name || client.contact_email || client.phone) && (
                  <>
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-3">Primary Contact</h4>
                      <div className="space-y-2">
                        {(client.first_name || client.last_name) && (
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span>{[client.first_name, client.last_name].filter(Boolean).join(' ')}</span>
                          </div>
                        )}
                        {client.contact_email && (
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span>{client.contact_email}</span>
                          </div>
                        )}
                        {client.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <span>{client.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <Separator />
                  </>
                )}

                {/* Dropbox Folder Section — one mapping per facility, since a client can ship to both */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-muted-foreground">Dropbox Folders</h4>
                  {documentFacilities.map((facility) => {
                    const folder = facilityFolders?.find((f) => f.facility_id === facility.id)?.dropbox_folder;
                    return (
                      <div key={facility.id}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-medium text-muted-foreground">{facility.code}</span>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={() => {
                                setDropboxDialogFacility(facility);
                                setDropboxDialogOpen(true);
                              }}
                            >
                              <Pencil className="h-3 w-3 mr-1" />
                              {folder ? "Change" : "Configure"}
                            </Button>
                          )}
                        </div>
                        <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                          <FolderOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          {folder ? (
                            <span className="text-sm font-medium truncate">{folder}</span>
                          ) : (
                            <span className="text-sm text-muted-foreground italic">Not configured</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <Separator />

                {/* Portal Tank Schedule: which facilities' tanks this client sees in the customer portal */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">Portal Tank Schedule</h4>
                  <p className="text-xs text-muted-foreground">
                    Facilities whose tanks this client can see in the customer portal. They always see tanks
                    holding their own wine.
                  </p>
                  <div className="flex flex-wrap gap-4 pt-1">
                    {allFacilities.map((facility) => {
                      const id = `portal-facility-${facility.id}`;
                      return (
                        <label key={facility.id} htmlFor={id} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            id={id}
                            checked={portalFacilityIds.includes(facility.id)}
                            disabled={!isAdmin || setPortalFacility.isPending}
                            onCheckedChange={(v) =>
                              setPortalFacility.mutate({
                                clientId: client.id,
                                facilityId: facility.id,
                                visible: v === true,
                              })
                            }
                          />
                          {facility.code}
                        </label>
                      );
                    })}
                  </div>
                </div>

                <Separator />

                <div className="space-y-4">
                  <h4 className="font-semibold">Statistics</h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <Wine className="h-5 w-5 text-primary" />
                      <div>
                        <p className="text-2xl font-bold">{stats?.activeLots || 0}</p>
                        <p className="text-xs text-muted-foreground">Active Lots</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-sm text-muted-foreground">Total Volume</p>
                    <p className="text-lg font-semibold">{stats?.totalVolume || 0} {volumeAbbrev}</p>
                  </div>
                </div>

                <Separator />

                {/* Wine Lots Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold flex items-center gap-2">
                      <Wine className="h-4 w-4" />
                      Wine Lots
                    </h4>
                    <span className="text-sm text-muted-foreground">
                      {stats?.activeLots || 0} total
                    </span>
                  </div>

                  {/* Search Input */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search lots by code or varietal..."
                      value={lotSearchTerm}
                      onChange={(e) => setLotSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>

                  {/* Lots List */}
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {lotsLoading ? (
                      <div className="text-center py-4 text-sm text-muted-foreground">
                        Loading lots...
                      </div>
                    ) : lots && lots.length > 0 ? (
                      lots.map((lot) => (
                        <div
                          key={lot.id}
                          className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <Wine className="h-4 w-4 text-primary flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="font-medium truncate">{lot.code}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {[lot.varietal, lot.vintage].filter(Boolean).join(" • ")}
                              </p>
                            </div>
                          </div>
                          <span className="text-sm text-muted-foreground flex-shrink-0">
                            {lot.current_volume_l ? Math.round(lot.current_volume_l).toLocaleString() : 0} {volumeAbbrev}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4 text-sm text-muted-foreground">
                        {lotSearchTerm ? "No lots match your search" : "No lots for this client"}
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleEdit} className="flex-1">
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </Button>
                    {isArchived ? (
                      <Button
                        variant="outline"
                        onClick={handleUnarchive}
                        disabled={unarchiveClient.isPending}
                        className="flex-1"
                      >
                        <ArchiveRestore className="mr-2 h-4 w-4" />
                        {unarchiveClient.isPending ? "Restoring..." : "Restore"}
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => setShowArchiveDialog(true)}
                        className="flex-1"
                      >
                        <Archive className="mr-2 h-4 w-4" />
                        Archive
                      </Button>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    onClick={() => setShowDeleteDialog(true)}
                    className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                    size="sm"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Permanently Delete
                  </Button>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client Permanently?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Are you sure you want to permanently delete <strong>{client.name}</strong>?
              </p>
              <p className="text-amber-600 font-medium">
                Warning: Clients with work orders, wine lots, or other associated records cannot be deleted.
                Use Archive instead to hide the client while preserving all historical data.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {!isArchived && (
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setShowArchiveDialog(true);
                }}
              >
                Archive Instead
              </Button>
            )}
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive {client.name}? The client will be hidden from
              active lists but can be restored later. All associated data (wine lots, work orders)
              will be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive} disabled={archiveClient.isPending}>
              {archiveClient.isPending ? "Archiving..." : "Archive Client"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DropboxFolderDialog
        open={dropboxDialogOpen}
        onOpenChange={setDropboxDialogOpen}
        client={client}
        facility={dropboxDialogFacility}
      />
    </>
  );
}
